import { useEffect, useState } from "react";
import client, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, X } from "lucide-react";

export function ShareDialog({ open, onOpenChange, project, onChanged }) {
  const [directory, setDirectory] = useState([]);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) client.get("/users/directory").then((r) => setDirectory(r.data));
  }, [open]);

  const collaborators = project.collaborators || [];
  const taken = new Set([project.owner_id, ...collaborators.map((c) => c.id)]);
  const candidates = directory.filter((u) => !taken.has(u.id) && (u.email + " " + u.name).toLowerCase().includes(query.toLowerCase())).slice(0, 6);

  const share = async (email) => {
    setBusy(true);
    try {
      const { data } = await client.post(`/projects/${project.id}/share`, { email });
      onChanged(data);
      setQuery("");
      toast.success(`${email} can now push this project`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };
  const unshare = async (uid) => {
    const { data } = await client.delete(`/projects/${project.id}/share/${uid}`);
    onChanged(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Share project</DialogTitle>
          <DialogDescription>Collaborators can push this project to Git. Only the owner or an admin can delete or share it.</DialogDescription>
        </DialogHeader>
        <Input data-testid="share-search" placeholder="Search a teammate by email or name…" value={query} onChange={(e) => setQuery(e.target.value)} className="h-10 border-slate-200" />
        {query && (
          <div className="divide-y divide-slate-100 rounded-md border border-slate-200" data-testid="share-candidates">
            {candidates.map((u) => (
              <button key={u.id} type="button" disabled={busy} onClick={() => share(u.email)} data-testid={`share-add-${u.email}`} className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50">
                <span><span className="font-medium text-slate-900">{u.name || u.email}</span> <span className="font-mono-tight text-xs text-slate-500">{u.email}</span></span>
                <UserPlus className="h-4 w-4 text-slate-400" />
              </button>
            ))}
            {!candidates.length && <p className="px-3 py-2 text-xs text-slate-500">No matching signed-up user.</p>}
          </div>
        )}
        <div className="mt-2">
          <p className="font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">collaborators · {collaborators.length}</p>
          <ul className="mt-2 space-y-1" data-testid="share-list">
            {collaborators.map((c) => (
              <li key={c.id} className="flex items-center justify-between rounded-md border border-slate-100 bg-slate-50/60 px-3 py-2 text-sm" data-testid={`collaborator-${c.email}`}>
                <span><span className="font-medium text-slate-900">{c.name || c.email}</span> <span className="font-mono-tight text-xs text-slate-500">{c.email}</span></span>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => unshare(c.id)} data-testid={`collaborator-remove-${c.email}`} aria-label="Remove"><X className="h-3.5 w-3.5" /></Button>
              </li>
            ))}
            {!collaborators.length && <li className="text-xs text-slate-500">Nobody yet — only you and admins can push.</li>}
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
}
